// Shared "flight-date-within-range" logic for fleet availability (Feature A)
// and time-phased base (Feature B1). One helper, used by both so the
// gap/overlap/inclusivity semantics can never drift between them.
//
// All ranges are evaluated against the REQUESTED FLIGHT DATE (not "today").
// Dates are plain YYYY-MM-DD strings. The requested flight date is already an
// Athens-local calendar date (booking flow stores data.date in Athens local
// time), and the from/to bounds are Athens-local calendar dates too, so a
// lexicographic compare is inherently Europe/Athens-correct at day
// granularity — no timezone math is required or wanted here.
//
// Pure module: no DOM, no React, no Sanity client. Safe to import from the
// React SPA (App.jsx), from Astro SSR, and from API routes alike.

const OPEN_ENDED = '9999-12-31';

// Normalise to a comparable YYYY-MM-DD string, or null if unusable.
// Accepts 'YYYY-MM-DD' or a fuller ISO 'YYYY-MM-DDThh:mm...' (datetime) and
// keeps only the calendar-date portion.
function asDate(v) {
  if (!v || typeof v !== 'string') return null;
  const d = v.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
}

// Is `flightDate` within [from, to], both inclusive? `to` empty/null means
// open-ended (ongoing). Returns false on unusable input rather than throwing.
export function isDateWithin(flightDate, from, to) {
  const d = asDate(flightDate);
  const f = asDate(from);
  if (!d || !f) return false;
  const t = asDate(to) || OPEN_ENDED;
  return f <= d && d <= t;
}

// Feature A — is the aircraft unavailable on the requested flight date because
// a maintenance/AOG window covers it? (Does NOT consider `hidden`; hidden is
// handled at the GROQ layer so hidden aircraft never reach the client.)
export function isAircraftUnavailableOn(aircraft, flightDate) {
  if (!aircraft || !flightDate) return false;
  const windows = Array.isArray(aircraft.unavailabilityWindows) ? aircraft.unavailabilityWindows : [];
  return windows.some(w => isDateWithin(flightDate, w?.from, w?.to));
}

// Feature B1 — resolve the base for a given flight date: the seasonal period
// whose range covers the date wins; otherwise fall back to homeBase. Returns
// the resolved base reference object (e.g. { id, name, city, type }) or null
// when neither a covering period nor a homeBase exists. With no flightDate
// (e.g. the /fleet listing has no date context) returns homeBase.
export function resolveBaseForDate(aircraft, flightDate) {
  if (!aircraft) return null;
  if (flightDate) {
    const periods = Array.isArray(aircraft.basePeriods) ? aircraft.basePeriods : [];
    const hit = periods.find(p => p?.base && isDateWithin(flightDate, p?.from, p?.to));
    if (hit) return hit.base;
  }
  return aircraft.homeBase || null;
}

// Convenience for display: short base label ("Athens Olympic Heliport") or ''.
export function baseLabel(base) {
  if (!base) return '';
  return base.name || base.city || base.id || '';
}
